import { throwError } from "@syla/shared/types/helpers/errors";
import { TransactionEvent } from "@syla/shared/types/responses/TransactionEvent";
import { WritableDraft } from "immer/dist/types/types-external";
import { parseErrorToUserMessage } from "../../api/errors/parsers/parseErrorToUserMessage";
import {
  UpdateTransaction,
  updateTransaction as updateTransactionRequest,
} from "../../api/transactions/updateTransaction";
import { captureRequestError } from "../../helper/captureRequestError";
import { checkStatusAndDoPendingWork } from "../pendingWork";
import {
  useTransactionsStore,
  TransactionStoreState,
} from "../transactionsStore";
import {
  refreshGroupList,
  updateTransactionInQueryCache,
  getGroupByTransactionId,
  invalidateTransactionQueryCache,
} from "./groupList";

export const updateTransaction = async ({
  accountId,
  id,
  updates: transactionUpdates,
}: UpdateTransaction) => {
  // store original state if we need to rollback
  const originalTransaction = getTransactionState(
    useTransactionsStore.getState(),
    id,
  ).data;

  // persist updates
  try {
    useTransactionsStore.setState((state) => {
      const transactionState = getTransactionState(state, id);
      transactionState.updating = true;
    });

    const updatedTransaction = await updateTransactionRequest({
      accountId,
      id,
      updates: transactionUpdates,
    });

    // The response contains the new state for the transactions (including the
    // _id for new ledgers which were created). So we can immediately update
    // the stored state.
    useTransactionsStore.setState((state) => {
      const transactionState = getTransactionState(state, id);
      transactionState.data = updatedTransaction;
      transactionState.updating = false;
      transactionState.error = undefined;
      setTransactionInGroups(state, updatedTransaction, false);
    });
    updateTransactionInQueryCache(id);
    invalidateTransactionQueryCache(accountId);

    refreshGroupList();

    checkStatusAndDoPendingWork();
  } catch (error) {
    captureRequestError(error);

    try {
      useTransactionsStore.setState((state) => {
        const transactionState = getTransactionState(state, id);
        transactionState.updating = false;
        transactionState.error = parseErrorToUserMessage(error).description;

        // rollback updates
        transactionState.data = originalTransaction;
      });
    } catch (error) {
      captureRequestError(error);
    }

    // refresh status from server in case of partial updates
    refreshGroupList();
  }
};

const setTransactionInGroups = (
  state: Pick<
    WritableDraft<TransactionStoreState>,
    "transactionToGroup" | "groups"
  >,
  transaction: WritableDraft<TransactionEvent>,
  updating: boolean,
) => {
  const transactionId = transaction._id;

  const { groupState } = getGroupByTransactionId(state, transactionId);

  const group = groupState.data;

  // update transaction
  for (let index = 0; index < group.transactions.length; index++) {
    if (group.transactions[index]._id === transactionId) {
      group.transactions[index] = transaction;
      break;
    }
  }

  groupState.updating = updating;
};

function getTransactionState(
  state: Pick<
    WritableDraft<TransactionStoreState> | TransactionStoreState,
    "transactions"
  >,
  id: string,
) {
  return (
    state.transactions?.[id] ??
    throwError(new Error(`Transaction ${id} not tracked`))
  );
}
